//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
import ReduxState from "../../declarations/state.d";
// suggestions-feed imports
import { IProps, IContainer, IState } from "./SuggestionsFeed.d";
import * as styles from "./SuggestionsFeed.styles";
// other internals
import { SuggestionCard } from "@components/suggestion-card";
import { IRemoveCallback } from "@components/card";
// util
import { ChromeAppWrapperView } from "@src/util/chrome-extension.util";
import { logger } from "@util/logger.util";
import { track, TrackingEvents } from "@util/analytics.util";
import { getCurrentOrg } from "@util/org.util";
import { PostMessageManager } from "@util/post-message.util";
import { isSuggestionInWhitelist } from "@util/suggestion.util";
// state
import { markSeenFeedSuggestion } from "@mutations";
import { suggestionsDebugFetchDate } from "@state/local-storage";
import {
  setSuggestionsFeedNotificationCount,
  setActiveView,
} from "@actions/chrome-extension.actions";

//////////////////
// COMPONENT
//////////////////
class SuggestionsFeedCmpt extends React.PureComponent<IProps, IState> {
  private refetchInterval: ReturnType<typeof setInterval>;
  // not needed for rendering, so avoid async state updates
  private lastRefetchTimestamp: number;

  constructor(props: IProps) {
    super(props);
    this.startRefetchPoll();
    // initialize to non-number so as to avoid updating before we have a real count
    this.lastRefetchTimestamp = props.lastRendererFetch || new Date().valueOf();
  }

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const { chromeExtensionIdle, lastRendererFetch } = this.props;
    if (lastRendererFetch && lastRendererFetch > this.lastRefetchTimestamp) {
      this.lastRefetchTimestamp = lastRendererFetch;
    }

    if (chromeExtensionIdle !== nextProps.chromeExtensionIdle) {
      if (nextProps.chromeExtensionIdle > 0) {
        // clear poll if idle
        this.clearIntervals();
      } else if (nextProps.chromeExtensionIdle === 0) {
        // restart poll if active
        this.startRefetchPoll();
      }
    }

    if (!this.props.fastCrawlFinished && nextProps.fastCrawlFinished) {
      this.lastRefetchTimestamp = new Date().valueOf();
      this.clearIntervals();
      this.refetch();
      this.startRefetchPoll();
    }
  }

  public componentDidUpdate() {
    this.updateCount();
  }

  public componentDidMount() {
    this.updateCount();
  }

  public componentWillUnmount() {
    // be a good JS engineer
    this.clearIntervals();
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private clearIntervals = () => {
    clearInterval(this.refetchInterval);
  };

  // handles updating count in all the various places
  private updateCount = () => {
    const { viewer } = this.props;
    const org = viewer && getCurrentOrg(viewer);
    if (!org) {
      return;
    }
    const suggestions =
      org.feedSuggestionsErrorable &&
      org.feedSuggestionsErrorable.__typename !== "GraphEntityError" &&
      org.feedSuggestionsErrorable.suggestions;

    if (!suggestions) {
      return;
    }

    const newCount = suggestions.reduce(
      (count, suggestion) =>
        count +
        (suggestion &&
        suggestion.occurredMillis > this.props.lastViewedFeedMillis &&
        isSuggestionInWhitelist(org, suggestion)
          ? 1
          : 0),
      0,
    );

    PostMessageManager.setSuggestionCount(newCount); // tell chrome
    if (newCount > 0) {
      this.props.setSuggestionsFeedNotificationCount(newCount);
      if (!this.props.chromeExtensionIdle) {
        // if not idle, go to feed
        this.props.setActiveView(ChromeAppWrapperView.Feed);
      }
    }

    if (this.props.updateCardCount) {
      const renderedSuggestions = this.getSuggestionsToRender(org, suggestions);
      this.props.updateCardCount(renderedSuggestions.length); // count callback to know if we need to set empty state
    }
  };

  // this handles the filtering of suggestions
  private getSuggestionsToRender = (org: ISchema.IOrg, suggestions: ISchema.IFeedSuggestion[]) => {
    // filter suggestions based on front-end whitelist
    const renderableSuggestions = suggestions.filter((suggestion) => {
      // null check because mutation updater just replaces object with null
      return !!suggestion && isSuggestionInWhitelist(org, suggestion);
    });
    return renderableSuggestions;
  };

  // ~-~-~-~-~-~-~-
  // Refetching
  // ~-~-~-~-~-~-~-
  private startRefetchPoll = () => {
    const FIFTEEN_MINUTES = 900000; // 15 minutes = 900,000 millis
    const now = new Date().valueOf();

    if (now - this.lastRefetchTimestamp > FIFTEEN_MINUTES) {
      this.refetch();
    }

    this.clearIntervals();
    this.refetchInterval = setInterval(this.refetch, FIFTEEN_MINUTES);
  };

  private refetch = () => {
    const debugFetchDateZonedMoment = suggestionsDebugFetchDate.get();
    const debugFetchDate = !!debugFetchDateZonedMoment
      ? debugFetchDateZonedMoment.valueOf()
      : undefined;

    this.props.relay.refetch(
      { debugFetchDate },
      {},
      (error) => {
        if (error) {
          logger.error("Could not refetch suggestions feed :(", error);
        } else {
          this.lastRefetchTimestamp = new Date().valueOf();
        }
      },
      { force: true },
    );
  };

  // ~-~-~-~-~-~-~-
  // Remove Suggestion
  // ~-~-~-~-~-~-~-
  private removeCard: IRemoveCallback = (suggestionArgs, trackingData) => {
    if (!suggestionArgs || !suggestionArgs.markSeenFeedSuggestionInput) {
      return;
    }

    const {
      feedSuggestionRelayId,
      primaryActionTaken,
    } = suggestionArgs.markSeenFeedSuggestionInput;
    const trackingAction = primaryActionTaken ? primaryActionTaken : "Dismissed";
    track(TrackingEvents.SUGGESTIONS.ACTION_CLICKED, {
      primaryActionTaken: trackingAction,
      ...trackingData,
    });

    markSeenFeedSuggestion(
      this.props.relay.environment,
      { feedSuggestionRelayId, primaryActionTaken },
      () => {
        return;
      },
      (error?: any) => logger.error("Failed to mark suggestion as seen", error),
    );
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  public render() {
    const { viewer } = this.props;
    const org = viewer && getCurrentOrg(viewer);

    if (!org || org.feedSuggestionsErrorable.__typename === "GraphEntityError") {
      logger.error("SuggestionsFeed", org ? org.feedSuggestionsErrorable : undefined, {
        orgIsFalsey: !org,
        viewerIsFalsey: !viewer,
      });
      // just show a blank feed if theres a load error
      return null;
    }

    const filteredSuggestions = this.getSuggestionsToRender(
      org,
      org.feedSuggestionsErrorable.suggestions,
    );

    return (
      <div style={styles.container}>
        {filteredSuggestions.map((s) => {
          return (
            <SuggestionCard removeCard={this.removeCard} feedSuggestion={s} org={org} key={s.id} />
          );
        })}
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: ReduxState.Redux, _ownProps: IContainer) {
  return {
    chromeExtensionIdle: state.webExtension.webExtensionIdle,
    chromeExtensionOpen: state.webExtension.webExtensionOpen,
    fastCrawlFinished: state.onboarding.fastCrawlFinished,
    lastViewedFeedMillis: state.chromeFeed.lastViewedFeedMillis,
  };
}

const reduxContainer = connect(mapStateToProps, {
  setSuggestionsFeedNotificationCount,
  setActiveView,
})(SuggestionsFeedCmpt);

//////////////////
// RELAY
//////////////////
import { fragments, query } from "./SuggestionsFeed.gql";

export const SuggestionsFeed = createRefetchContainer<IContainer>(reduxContainer, fragments, query);
